'use client'

import { FilterGroupMobile } from '@/app/[locale]/collection/_components/filter/filter-group/filter-group-mobile'
import { Select } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { scrollToProductSection } from '@/lib/utils/scroll-utils'
import { TCollectionData } from '@/types/collection'
import { useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import { useShallow } from 'zustand/react/shallow'
import {
  COLLECTION_SORT_LABELS,
  ECollectionSort,
  shouldHideSorting,
} from '../../../constants'
import { useCollectionData } from '../../../hooks/use-collection-data'
import { useCollectionFilterStoreV2 } from '../../../store/collection-filter-store-v2'
import {
  FilterTag,
  getFilterTagsFromUrl,
} from '../../../utils/filter-tag-utils'
import { getFilterStatusFromFacets } from '../../../utils/filter-utils'
import { FilterBarTags } from '../filter-bar-tags'

type Props = {
  className?: string
  serverData: TCollectionData
  initialFilters?: Record<string, string | string[] | number | undefined>
}

export const FilterSummaryBarMobile = ({
  className,
  serverData,
  initialFilters,
}: Props) => {
  const { filters, setFilter, clearFilters, concatValue, setConcatValue } =
    useCollectionFilterStoreV2(
      useShallow((state) => ({
        filters: state.filters,
        setFilter: state.setFilter,
        clearFilters: state.clearFilters,
        concatValue: state.concatValue,
        setConcatValue: state.setConcatValue,
      }))
    )

  // Get facets from serverData
  const facets = serverData?.collection_detail?.facets || []
  const card_facets = serverData?.collection_detail?.card_facets || []

  const searchParams = useSearchParams()
  const t = useTranslations('collection_page')

  // Get collection data with product count
  const { data } = useCollectionData({ serverData, initialFilters })
  const productCount =
    data?.pages?.[data.pages.length - 1]?.pagination?.total ?? 0

  const onClearFilter = () => {
    clearFilters()

    // Cuộn lên đầu danh sách sản phẩm sau khi xóa toàn bộ filter
    scrollToProductSection(200, 50)
  }

  const handleSortChange = (value: string) => {
    setFilter('sort', value)

    // Cuộn lên đầu danh sách sản phẩm sau khi thay đổi sort
    scrollToProductSection(200, 50)
  }

  const onRemoveTag = (tag: FilterTag) => {
    const currentValue = filters[tag.parentKey]

    if (Array.isArray(currentValue)) {
      const newValues = currentValue.filter((v) => v !== tag.value)
      setFilter(tag.parentKey, newValues)
    } else {
      // Kiểm tra xem filter này có phải là concat slug không
      const facet = facets.find((f) => f.seo_alias === tag.parentKey)
      const cardFacet = card_facets.find((f) => f.seo_alias === tag.parentKey)
      if (facet?.is_concat_slug || cardFacet?.is_concat_slug) {
        // Nếu là concat slug, cần clear concatValue
        setConcatValue(null)
      }
      setFilter(tag.parentKey, undefined)
    }

    // Cuộn lên đầu danh sách sản phẩm sau khi xóa filter tag
    scrollToProductSection(200, 50)
  }

  // Get tags directly from URL instead of store
  const currentUrl =
    typeof window !== 'undefined' ? new URL(window.location.href) : null
  const originalSlug = useCollectionFilterStoreV2((state) => state.originalSlug)
  const tags =
    currentUrl && originalSlug
      ? getFilterTagsFromUrl(currentUrl, originalSlug, facets, card_facets)
      : []
  const { hasFilter } = getFilterStatusFromFacets(
    filters,
    facets || undefined,
    card_facets || undefined
  )
  const hideSorting = shouldHideSorting(originalSlug)

  if (!facets && !card_facets) {
    return null
  }

  return (
    <div
      className={cn(
        'sticky top-0 z-50 bg-light px-5 pb-4 pt-3.5 shadow-[0_4px_12px_0_rgba(0,0,0,0.08)]',
        className
      )}
    >
      <div className='flex items-center justify-between'>
        <p className='font-sans text-sm leading-4.5 text-neutral-500'>
          {t('title.count_result', {
            count: productCount,
          })}
        </p>

        <div className='flex items-center gap-2'>
          {!hideSorting && (
            <Select
              options={Object.entries(COLLECTION_SORT_LABELS).map(
                ([key, label]) => ({
                  label: t(`sort_label.${label}`),
                  value: key,
                })
              )}
              onValueChange={handleSortChange}
              defaultValue={searchParams.get('sort') || ECollectionSort.DEFAULT}
              className={{
                trigger: 'min-w-[106px] border-neutral-300 px-3 py-2.5',
              }}
            />
          )}
          <FilterGroupMobile facets={facets} card_facets={card_facets} />
        </div>
      </div>

      {hasFilter && (
        <div className='mt-2.5 flex items-center gap-3 border-t border-t-neutral-200 pt-2'>
          <button
            className='p-2 font-sans text-sm font-semibold leading-4.5 text-primary/90'
            onClick={onClearFilter}
          >
            {t('button.clear_filter')}
          </button>
          <FilterBarTags
            tags={tags}
            onRemoveTag={onRemoveTag}
            className='flex-1 flex-nowrap overflow-auto border-0 pb-0'
          />
        </div>
      )}
    </div>
  )
}
